import { Conditions, CoverageType } from '@/lib/types/types'
import { i18n, Locale } from '@/i18n/i18n-config'
import moment from 'moment'

/**
 * Retrieves the textual representation of coverage types based on locale and translation.
 *
 * @param types - An array of coverage type strings.
 * @param locale - The locale for which the translation is needed.
 * @param translation - An object containing translations for coverage types.
 * @returns A string with translated coverage types, joined by " + " if there are multiple.
 */
export const getCoverageTypeText = (types: string[], locale: string, translation: CoverageType): string => {
    const getTranslation = (type: string): Translation =>
        translation[type.toLocaleLowerCase() as keyof typeof translation]

    const translate = (type: string) => t(getTranslation(type), locale)

    return types.length === 1 ? translate(types[0]) : types.map(translate).join(' + ')
}

/**
 * Masks a number, leaving only the last 4 digits visible.
 *
 * @param number - The number to be masked.
 * @returns The masked number in the format "**** 1234".
 */
export function maskNumber(number?: string | number) {
    if (!number) return ''

    const strNum = String(number)

    if (strNum.length < 4) return ''

    const lastFour = strNum.slice(-4)

    return `**** ${lastFour}`
}

/**
 * Returns the logo path based on the card type.
 *
 * @param cardType - The card type string.
 * @returns The path to the card logo, or a default logo if the type is not recognized.
 **/
export function getCardLogo(cartType?: string): string {
    switch (cartType) {
        case 'Visa':
            return '/images/cardsLogo/visa.png'
        case 'MasterCard':
            return '/images/cardsLogo/mastercard.png'
        default:
            return '/images/cardsLogo/iban.png'
    }
}

/**
 * Determines the card type based on the card number.
 *
 * @param cardNumber - The credit card number as a string.
 * @returns The detected card type, or 'Unknown' if the pattern doesn't match.
 **/
export function getCardType(cardNumber: string): string {
    const patterns: { [key: string]: RegExp } = {
        Visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
        MasterCard: /^5[1-5][0-9]{14}$/,
        'American Express': /^3[47][0-9]{13}$/, 
        'Diners Club': /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/, 
        Discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
        JCB: /^(?:2131|1800|35\d{3})\d{11}$/, 
    }

    for (const [cardType, pattern] of Object.entries(patterns)) {
        if (pattern.test(cardNumber)) {
            return cardType
        }
    }

    return 'Unknown'
}

/**
 • Returns an icon name based on the insurance type.
 *
 • @param type - The insurance type string.
 • @returns The name of the icon to be used, or an empty string if the type is not recognized.
 **/
 export const getInsuranceTypeIcon = (type: string): string => {
    switch (type) {
        case 'Check24 Telco mobilephone insurance':
        case 'Check24 Mobilephone Insurance_v3':
        case 'Tekimpact electronics insurance':
        case 'Mobile insurance':
            return 'phone-in-policy';
        case 'Bicycle insurance':
            return 'bicycle';
        default:
            return 'phone-in-policy';
    }
};
/**
 * Checks if a given period is valid based on the start and end dates.
 *  
 * @param dateStart - The start date of the period.
 * @param dateEnd - The end date of the period.
 * @returns `true` if the period is valid, otherwise `false`.
 */

export const periodIsValid = (dateStart?: string, dateEnd?: string): boolean =>
    moment(dateStart, 'YYYY-MM-DD').isValid() && moment(dateEnd, 'YYYY-MM-DD').isValid()

/**
 * Mapping of error codes to error keys.
 */
export const errorCodeToErrorKey = {
    500: 'generic',
    429: 'rateLimit',
    401: 'invalidToken',
    400: 'invalidCredentials',
    303: 'oldFlow',
}

/**
 * Retrieves the error key associated with a given status code.
 */
export const getErrorKeyByStatusCode = (statusCode: number, errorCodeToErrorKey: Record<number, string>): string => {
    if (Object.hasOwn(errorCodeToErrorKey, statusCode)) {
        return errorCodeToErrorKey[statusCode as keyof typeof errorCodeToErrorKey];
    }
    return 'generic';
};

/**
 * Represents a translation object that maps locales to strings.
 */
type Translation = {
    [key in Locale]: string
}

/**
 * Translates the given translation object based on the specified locale.
 * If the translation for the specified locale is not found, it falls back to the default locale.
 *
 * @param translation - The translation object containing translations for different locales.
 * @param locale - The locale for which to retrieve the translation.
 * @param variables - An optional object containing key-value pairs to dynamically replace placeholders in the translation string.
 * @returns The translated string.
 */
export const t = (translation: Translation, locale: Locale, variables: { [key: string]: string | number } = {}): string => {
    const translationLocale = locale || i18n.defaultLocale

    if (!translation) {
        return ''
    }

    let translationText = translation[translationLocale] || ''

    if (typeof variables === 'object' && variables) {
        Object.keys(variables).forEach(key => {
            const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g')
            translationText = translationText.replace(regex, variables[key].toString())
        })
    }

    return translationText
}

/**
 * Translates a given translation object based on the specified locale.
 * Supports dynamic placeholder replacements with strings, numbers, and JSX elements.
 * If a placeholder value is a function, it will be called to generate JSX.
 * If the translation for the specified locale is not found, it falls back to the default locale.
 *
 * @param translation - The translation object containing translations for different locales.
 * @param locale - The locale for which to retrieve the translation.
 * @param variables - An optional object containing key-value pairs to dynamically replace placeholders in the translation string.
 *                     - Strings and numbers are directly substituted.
 *                     - Functions return JSX elements for rich formatting.
 * @returns An array of strings and JSX elements forming the translated message.
 */
export const tRich = (
    translation: Translation,
    locale: Locale,
    variables: { [key: string]: string | number | ((chunk: string) => JSX.Element) } = {}
): (string | JSX.Element)[] => {
    const translationLocale = locale || i18n.defaultLocale;

    if (!translation) {
        return [];
    }

    const translationText = translation[translationLocale] || '';

    const tokenRegex = /({{\s*\w+\s*}}|\*\*(.+?)\*\*|_(.+?)_)/g;

    const result: (string | JSX.Element)[] = [];
    let lastIndex = 0;

    let match: RegExpExecArray | null;
    
    while ((match = tokenRegex.exec(translationText)) !== null) {
        const [fullMatch, , boldText, italicText] = match;
        const matchIndex = match.index;

        if (lastIndex < matchIndex) {
            result.push(translationText.slice(lastIndex, matchIndex));
        }

        if (fullMatch.startsWith('{{')) {
            const varMatch = fullMatch.match(/{{\s*(\w+)\s*}}/);
            if (varMatch) {
                const key = varMatch[1];
                const value = variables[key];

                if (typeof value === 'function') {
                    result.push(value(key));
                } else if (typeof value === 'number') {
                    result.push(value.toString());
                } else {
                    result.push(value ?? fullMatch);
                }
            }
        } else if (boldText) {
            result.push(<b key={`b-${matchIndex}`}>{boldText}</b>);
        } else if (italicText) {
            result.push(<i key={`i-${matchIndex}`}>{italicText}</i>);
        }

        lastIndex = tokenRegex.lastIndex;
    }

    if (lastIndex < translationText.length) {
        result.push(translationText.slice(lastIndex));
    }

    return result;
};

/**
 * Checks if an object is empty.
 * @param obj - The object to check.
 * @param checkValues - Optional parameter to check if the values of the object are also empty.
 * @returns Returns true if the object is empty, false otherwise.
 */
export const isObjectEmpty = (obj: Record<string, any> | undefined | null | boolean, checkValues?: boolean): boolean => {
    if (!obj) {
        return true
    }

    const hasKeys = Object.keys(obj).length > 0

    if (Object.keys(obj).length === 0) {
        return true;
    }

    if (checkValues) {
        return Object.values(obj).every(value => !value);
    }

    return false
}

/**
 * Checks if an array is empty.
 * @param arr - The array to check.
 * @returns True if the array is empty, false otherwise.
 */
export const isArrayEmpty = (arr: unknown[] | undefined | null): boolean => {
    if (!arr) {
        return true
    }

    return arr.length === 0
}

/**
 * Converts a kebab-case string to camelCase.
 *
 * @param str - The kebab-case string to convert.
 * @returns The camelCase version of the input string.
 */
export const camelizeKebabCase = (str: string): string => str.replace(/-./g, x => x[1].toUpperCase())

/**
 * Converts a string to kebab case.
 * 
 * @param str - The input string to convert.
 * @returns The kebab case version of the input string.
 */
export const kebabCase = (str: string): string => str
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, '-')
    .toLowerCase()

/**
 * Converts a snake_case string to camelCase.
 * @param str - The snake_case string to convert.
 * @returns The camelCase version of the input string.
 */
export const camelizeSnakeCase = (str: string): string => str.replace(/_./g, x => x[1].toUpperCase())

/**
 * Converts a string to snake case.
 * @param str - The string to convert.
 * @returns The snake case version of the input string.
 */
export const snakeCase = (str: string): string => str
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, '_')
    .toLowerCase()

/**
 * Converts a File object to a base64 string.
 * @param file - The File object to convert.
 * @returns A Promise that resolves to the base64 string representation of the file.
 */
export const getBase64 = async (file: File): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader()

        reader.readAsDataURL(file)
        reader.onload = () => {
            resolve(reader.result as string)
        }

        reader.onerror = reject
    }).catch((error) => {
        throw new Error('Failed to convert file to base64.')
    })
}

/**
 * Removes the base64 prefix from a base64 string.
 * 
 * @param base64String - The base64 string with the prefix.
 * @returns The base64 string without the prefix.
 */
export const stripBase64Prefix = (base64String: string): string => {
    return base64String.split(',')[1]
}

export const addBase64Prefix = (base64String: string, ctype: string): string => {
    return `data:${ctype};base64,${base64String}`
}

/**
 * Validates the previous steps in a flow based on the current step, conditions, and flow array.
 * 
 * @param currentStep - The current step in the flow.
 * @param conditions - The conditions object containing the completion status of each step.
 * @param flow - The array representing the flow of steps.
 * @returns A boolean indicating whether all previous steps are completed.
 */
export const validatePreviousSteps = (currentStep: string, conditions: Conditions, flow: string[]): boolean => {
    const previousSteps = flow.slice(0, flow.indexOf(currentStep))

    return previousSteps.every((step) => {
        const stepConditions = conditions[step]

        return stepConditions.completed
    })
}

/**
 * Capitalizes the first letter of a string.
 * 
 * @param str - The input string.
 * @returns The input string with the first letter capitalized.
 */
export const capitalizeFirstLetter = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1)

/**
 * Lower case the first letter of a string.
 * 
 * @param str - The input string.
 * @returns The input string with the first letter lower cased.
 */
export const lowerCaseFirstLetter = (str: string): string => str.charAt(0).toLowerCase() + str.slice(1)

/**
 * Filters an object for keys that have a specific value.
 * 
 * @param obj - The object to filter.
 * @param searchValue - The value to search for.
 * @returns An array of keys that have the specified value.
 */
export const filterObjectForKeysWithValue = (obj: Record<string, unknown>, searchValue: string): string[] => {
    return Object.keys(obj).filter(key => obj[key] === searchValue);
}

/**
 * Checks if the browser is Samsung Internet.
 * 
 * @returns `true` if the user is browsing with Samsung Internet, otherwise `false`.
 */
export const checkSamsungBrowser = (): boolean => {
    if (typeof navigator !== 'undefined') {
        return /SamsungBrowser/i.test(navigator.userAgent);
    }
    return false;
};

/**
 * Detects the MIME type of a given file based on its extension or built-in type.
 * 
 * @param file - The file whose MIME type needs to be determined.
 * @returns A string representing the detected MIME type.
 */
export const detectMimeType = (file: File): string => {
    if (file.type) return file.type;

    const extension = file.name.split('.').pop()?.toLowerCase();
    const extensionToMime: Record<string, string> = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        heic: 'image/heic',
        heif: 'image/heif',
        webp: 'image/webp',
        pdf: 'application/pdf'
    };

    return extension ? extensionToMime[extension] : 'application/octet-stream';
};

/**
 * Converts a given file to a Blob.
 * 
 * @param file - The file to be converted.
 * @returns A Promise resolving to a Blob containing the file data.
 */
export const fileToBlob = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve(new Blob([reader.result as ArrayBuffer], { type: file.type }));
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });
};

/**
 * Converts a HEIC/HEIF image file to JPEG format.
 * 
 * @param file - The HEIC or HEIF file to be converted.
 * @returns A Promise resolving to a new File object in JPEG format.
 */
export const convertHEICToJPEG = async (file: File): Promise<File> => {
    const heic2any = require('heic2any');

    try {
        const fileBlob = file instanceof Blob ? file : await fileToBlob(file);

        const jpegBlob = await heic2any({ blob: fileBlob, toType: 'image/jpeg' });

        return new File([jpegBlob], file.name.replace(/\.heic|\.heif/i, '.jpeg'), { type: 'image/jpeg' });
    } catch (err) {
        console.error('HEIC conversion error:', err);
        return file;
    }
};

/**
 * Converts a file's content URI to a Blob.
 * 
 * @param file - The file to be converted.
 * @returns A Promise resolving to a Blob containing the file's data.
 */
export const convertContentUriToBlob = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => reader.result
            ? resolve(new Blob([reader.result], { type: detectMimeType(file) }))
            : reject();
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });
};

/**
 * Checks if a given file is in HEIC or HEIF format.
 * 
 * @param file - The file to be checked.
 * @returns `true` if the file is in HEIC or HEIF format, otherwise `false`.
 */
export const isHEIC = (file: File): boolean => {
    const x = file.type ? file.type.split('image/').pop() : file.name.split('.').pop()?.toLowerCase();

    return x === 'heic' || x === 'heif';
};

/**
 • Returns formatted sum with the correct currency symbol based on ISO code
 *
 • @param sum - Amount 
 • @param currency - Currency ISO code
 • @returns Currency symbol
 **/
 export const getFormattedSum = (sum: string | number = 0, currency: string = 'EUR'): string => {
    sum = sum.toString()
    const formattedSum = sum.includes('.') ? sum.replace('.', ',') : sum;

    const currencySymbols: Record<string, string> = {
        USD: '$',
        EUR: '€',
        GBP: '£'
    };

    const symbol = currencySymbols[currency.toUpperCase()] || "€";

    return `${formattedSum} ${symbol}`;
};

/**
 • Returns formatted payment method
 *
 • @param info - method info
 • @returns type and digits
 **/
 export const getFormattedPaymentMethod = (info: string) => {
    const lowerCaseMethod = info.toLowerCase();
    const lastDigitsMatch = info.match(/\d+$/); 
    let lastDigits = lastDigitsMatch ? lastDigitsMatch[0] : '';

    lastDigits = lastDigits.padStart(4, 'xx');

    const formattedDigits = `xxxx ${lastDigits}`;

    if (lowerCaseMethod.includes('visa')) {
        return { cardType: 'Visa', cardNumber: formattedDigits, iban: '' };
    }

    if (lowerCaseMethod.includes('master')) {
        return { cardType: 'MasterCard', cardNumber: formattedDigits, iban: '' };
    }

    return { cardType: '', cardNumber: '', iban: formattedDigits };
};


/**
 • Returns boolean if card/iban method is allowed
 *
 • @param methods - array of payment methods
 • @param type - iban/card
 • @returns boolean
 **/
 export const hasPaymentType = (methods: string[], type: 'card' | 'iban') => {
    
    if (type === 'card') {
        const cardMethods = ['CompuTopSix3DS1', 'CompuTopSix', 'StripeCreditCard']
        return methods.some(item => cardMethods.includes(item));
    }

    if (type === 'iban') {
        const ibanMethods = ['GoCardlessDirectDebit', 'StripeSepa']
        return methods.some(item => ibanMethods.includes(item));
    }

    return false;

};

/**
 • Returns provider for card payment
 *
 • @param methods - array of payment methods
 • @returns string
 **/
 export const getCardPaymentProvider = (methods: string[]) => {
    
    if (methods.includes('StripeCreditCard')) {
        return 'StripeCreditCard';
    }

    if (methods.includes('CompuTopSix')) {
        return 'CompuTopSix';
    }

    if (methods.includes('CompuTopSix3DS1')) {
        return 'CompuTopSix3DS1';
    }

    return '';

};
  
/**
 • Returns boolean if a new payment method of the given type can be added.
 • GoCardless mandates can no longer be created since the move to Stripe — existing
 • ones stay usable, but the API rejects any new one.
 *
 • @param methods - array of payment methods
 • @param type - iban/card
 • @returns boolean
 **/
 export const canAddPaymentType = (methods: string[], type: 'card' | 'iban') => {
    const deprecatedMethods = ['GoCardlessDirectDebit']

    return hasPaymentType(methods.filter(item => !deprecatedMethods.includes(item)), type);

};
